import { useState } from 'react';
import { Alert, Button, ButtonBase, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import ConfirmDeleteDialog from '../../../components/ConfirmDeleteDialog';
import type { Booking, Machine } from '../types';
import { AVAILABILITY, getBlockingInUseBooking, getBookingPickupState, isBookingOverdueReturn } from '../constants';
import { BOOKING_DETAIL_LABELS as LABELS, BOOKING_PICKUP_LABELS, PLANNING_LABELS } from '../../../utils/labels';
import { formatDateRange, formatDateWithTime } from '../../../utils/formatDateTime';
import { useNotification } from '../../../context/useNotification';

// pickup_confirmed_at/handin_confirmed_at are full ISO timestamps (timestamptz), unlike
// formatDateWithTime's plain YYYY-MM-DD + HH:mm inputs — split before formatting.
function formatIsoTimestamp(iso: string): string {
  const [date, time] = iso.split('T');
  return formatDateWithTime(date, time?.slice(0, 5));
}

interface BookingDetailPanelProps {
  booking: Booking;
  machine: Machine;
  isReadOnly: boolean;
  pickupHandinEnabled: boolean;
  onBack: () => void;
  onClose: () => void;
  onEdit: (bk: Booking) => void;
  onDelete: (id: string) => Promise<void>;
  onConfirmPickup: (bk: Booking) => void;
  onConfirmHandin: (bk: Booking) => void;
}

export default function BookingDetailPanel({
  booking,
  machine,
  isReadOnly,
  pickupHandinEnabled,
  onBack,
  onClose,
  onEdit,
  onDelete,
  onConfirmPickup,
  onConfirmHandin,
}: BookingDetailPanelProps) {
  const [pendingDelete, setPendingDelete] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { notifySuccess, notifyError } = useNotification();

  const bkColor = booking.sites?.color ?? '#a8a29e';
  const userName = [booking.subcontractor_user?.first_name, booking.subcontractor_user?.last_name]
    .filter(Boolean)
    .join(' ');
  const pickupState = getBookingPickupState(booking, pickupHandinEnabled);
  const overdue = isBookingOverdueReturn(booking, pickupHandinEnabled);
  const blockingBooking = pickupState === 'reserved' ? getBlockingInUseBooking(booking, machine.bookings ?? []) : null;
  const pickupContactName = [booking.pickup_subcontractor_user?.first_name, booking.pickup_subcontractor_user?.last_name]
    .filter(Boolean)
    .join(' ');
  const handinContactName = [booking.handin_subcontractor_user?.first_name, booking.handin_subcontractor_user?.last_name]
    .filter(Boolean)
    .join(' ');
  const breadcrumbLabel = [machine.name, machine.category].filter(Boolean).join(' · ');

  return (
    <>
      {/* Header */}
      <div className="flex items-start gap-2 px-4 py-3 shrink-0 border-b border-border">
        <div className="flex flex-col min-w-0 flex-1">
          <ButtonBase
            onClick={onBack}
            className="flex items-center justify-start gap-1 text-sm text-text-secondary rounded w-full min-w-0"
            data-testid="booking-panel-back"
          >
            <ArrowBackIcon fontSize="inherit" className="shrink-0" />
            <span className="truncate min-w-0">{breadcrumbLabel}</span>
          </ButtonBase>
          <p id="detail-booking-title" className="text-lg font-semibold mb-0 mt-0 truncate">
            {booking.subcontractor?.name ?? ''}
          </p>
        </div>
        {!isReadOnly && (
          <Tooltip title={LABELS.actionsMenu}>
            <IconButton
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              color="inherit"
              aria-label={LABELS.actionsMenu}
              aria-haspopup="true"
              aria-expanded={!!menuAnchor}
              data-testid="actions-menu-button"
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={LABELS.close}>
          <IconButton onClick={onClose} color="inherit" aria-label={LABELS.close}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </div>

      {!isReadOnly && (
        <Menu
          anchorEl={menuAnchor}
          open={!!menuAnchor}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onEdit(booking);
            }}
            data-testid="edit-button"
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{LABELS.edit}</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setPendingDelete(true);
            }}
            data-testid="delete-button"
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>{LABELS.delete}</ListItemText>
          </MenuItem>
        </Menu>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {overdue && (
          <Alert severity="warning">{BOOKING_PICKUP_LABELS.overdueBanner}</Alert>
        )}
        {blockingBooking && (
          <Alert severity="warning" data-testid="pickup-blocked-warning">
            {BOOKING_PICKUP_LABELS.blockedByBooking(blockingBooking.subcontractor?.name ?? '')}
          </Alert>
        )}

        <div className="space-y-3">
          {pickupState && (
            <div className="flex flex-col gap-0.5">
              <span className="text-sm text-text-secondary">{LABELS.status}</span>
              <div className="flex items-center gap-1.5">
                {(pickupState === 'reserved' || pickupState === 'in_use') && (
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${AVAILABILITY[pickupState].dot}`} />
                )}
                <span className="text-base">
                  {pickupState === 'reserved'
                    ? BOOKING_PICKUP_LABELS.statusReserved
                    : pickupState === 'in_use'
                    ? BOOKING_PICKUP_LABELS.statusInUse
                    : BOOKING_PICKUP_LABELS.statusReturned}
                </span>
              </div>
            </div>
          )}
          {userName && (
            <div className="flex flex-col gap-0.5">
              <span className="text-sm text-text-secondary">{LABELS.contact}</span>
              <span className="text-base truncate">{userName}</span>
            </div>
          )}
          {booking.sites && (
            <div className="flex flex-col gap-0.5">
              <span className="text-sm text-text-secondary">{LABELS.site}</span>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: bkColor }} />
                <span className="text-base truncate">{booking.sites.name}</span>
              </div>
            </div>
          )}
          <div className="flex flex-col gap-0.5">
            <span className="text-sm text-text-secondary">{LABELS.period}</span>
            <span className="text-base">
              {formatDateRange(booking.start_date, booking.start_time, booking.end_date, booking.end_time)}
            </span>
          </div>
          {booking.notes && (
            <div className="flex flex-col gap-0.5">
              <span className="text-sm text-text-secondary">{LABELS.notes}</span>
              <span className="text-base">{booking.notes}</span>
            </div>
          )}
          {pickupContactName && booking.pickup_confirmed_at && (
            <>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm text-text-secondary">{BOOKING_PICKUP_LABELS.pickedUpByLabel}</span>
                <span className="text-base">{pickupContactName}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm text-text-secondary">{BOOKING_PICKUP_LABELS.pickedUpAtLabel}</span>
                <span className="text-base">{formatIsoTimestamp(booking.pickup_confirmed_at)}</span>
              </div>
            </>
          )}
          {handinContactName && booking.handin_confirmed_at && (
            <>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm text-text-secondary">{BOOKING_PICKUP_LABELS.handedInByLabel}</span>
                <span className="text-base">{handinContactName}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm text-text-secondary">{BOOKING_PICKUP_LABELS.handedInAtLabel}</span>
                <span className="text-base">{formatIsoTimestamp(booking.handin_confirmed_at)}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer — at most one primary action */}
      {!isReadOnly && pickupState === 'reserved' && (
        <div className="p-4 border-t border-border shrink-0">
          <Tooltip title={blockingBooking ? BOOKING_PICKUP_LABELS.pickupNotPossible : ''}>
            <span className="block w-full">
              <Button
                fullWidth
                size="large"
                variant="contained"
                startIcon={<ArrowCircleUpIcon />}
                onClick={() => onConfirmPickup(booking)}
                data-testid="confirm-pickup-button"
                disabled={!!blockingBooking}
              >
                {BOOKING_PICKUP_LABELS.actionConfirmPickup}
              </Button>
            </span>
          </Tooltip>
        </div>
      )}
      {!isReadOnly && pickupState === 'in_use' && (
        <div className="p-4 border-t border-border shrink-0">
          <Button
            fullWidth
            size="large"
            variant="contained"
            startIcon={<ArrowCircleDownIcon />}
            onClick={() => onConfirmHandin(booking)}
            data-testid="confirm-handin-button"
          >
            {BOOKING_PICKUP_LABELS.actionConfirmHandin}
          </Button>
        </div>
      )}

      <ConfirmDeleteDialog
        open={pendingDelete}
        onClose={() => setPendingDelete(false)}
        onConfirm={() => {
          setPendingDelete(false);
          onDelete(booking.id)
            .then(() => notifySuccess(PLANNING_LABELS.bookingDeleteSuccess))
            .catch(() => notifyError(PLANNING_LABELS.mutationError));
          onBack();
        }}
      />
    </>
  );
}
